import { useEffect, useState } from "react";
import { Mail, Phone, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "in_review", "responded", "closed"];

const statusBadge = (s) => {
  if (s === "new") return "bg-primary/15 text-primary border-primary/30";
  if (s === "in_review") return "bg-amber-500/15 text-amber-700 border-amber-500/30";
  if (s === "responded") return "bg-emerald-600/15 text-emerald-700 border-emerald-600/30";
  return "bg-muted text-muted-foreground border-border";
};

export default function RequestsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/admin/requests"); setItems(data || []); }
    catch { toast.error("Failed to load requests"); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/requests/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Could not update"); }
  };

  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);

  return (
    <div className="space-y-8" data-testid="requests-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Inbox</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">Service Requests</h1>
          <p className="text-muted-foreground mt-2">Inbound enquiries from the public website.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-sm h-11 w-56" data-testid="requests-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>))}
          </SelectContent>
        </Select>
      </header>

      <div className="grid md:grid-cols-2 gap-5" data-testid="requests-grid">
        {filtered.length === 0 && (
          <Card className="rounded-sm p-10 text-center text-muted-foreground col-span-full">
            No requests in this view.
          </Card>
        )}
        {filtered.map((r) => (
          <Card key={r.id} className="rounded-sm p-6 hover:border-primary transition-colors" data-testid={`request-card-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-lg">{r.name}</div>
                <div className="text-xs text-muted-foreground tracking-[0.15em] uppercase mt-1">{r.service}</div>
              </div>
              <Badge variant="outline" className={`rounded-sm capitalize ${statusBadge(r.status)}`}>
                {r.status.replace("_", " ")}
              </Badge>
            </div>
            <p className="mt-5 text-sm text-foreground/80 leading-relaxed line-clamp-4">{r.message}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>
              <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(r.created_at).toLocaleString()}</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(r.id, s)}
                  className={`text-xs px-3 py-1.5 border rounded-sm capitalize transition-colors ${
                    r.status === s
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border hover:border-primary hover:text-primary"
                  }`}
                  data-testid={`request-status-btn-${r.id}-${s}`}
                >
                  {r.status === s && <Check className="inline h-3 w-3 mr-1" />}
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
